import { useCallback, useMemo } from 'react'

import { SkeletonBox } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/react'
import { useCurrentCurrency } from '@features/locales/hooks/currency'
import { FullScreen } from '@modals/layout'
import { ENTITY_WITHDRAWAL_LIMITS_ENABLED } from '@services/permissions'
import { routes } from '@services/sections/modal/routes'
import { BurnFeeV2Banner } from '@shared/components/common'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Center } from '@shared/components/layout'
import {
  Currency,
  useEntityLimitsQuery,
  WireTransferLimitDurationEnum,
  WireTransferTypeEnum,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { SelectAmountForm } from './SelectAmount.Form'
import { SelectAmountFormLegacy } from './SelectAmountLegacy.Form'

export const SelectAmount: React.FC = () => {
  const { t } = useTranslation('modals.payout')
  const { router } = useModal()
  const [{ currency }] = useCurrentCurrency()
  const [, { isAuthorized: isEntityWithdrawalLimits }] = usePermission(
    ENTITY_WITHDRAWAL_LIMITS_ENABLED,
  )

  const {
    data: limitData,
    loading: limitLoading,
    error: limitError,
    refetch: limitRefetch,
  } = useEntityLimitsQuery({
    variables: {
      currency: currency ?? Currency.USDC,
      type: WireTransferTypeEnum.withdrawal,
    },
    skip: !isEntityWithdrawalLimits,
    fetchPolicy: 'cache-and-network',
  })

  const handleAmountSubmit = useCallback(() => {
    router.push(routes.transfer.withdraw.chooseRecipient)
  }, [router])

  // Default duration to weekly for payouts
  const duration = useMemo(() => {
    return limitData
      ? limitData.entityLimits?.duration
      : WireTransferLimitDurationEnum.weekly
  }, [limitData])

  return (
    <FullScreen totalSteps={5}>
      <Center className="font-circular" variant="horizontal">
        <BurnFeeV2Banner className="mb-6" multiline />
        <h2 className="mb-2 text-center text-2xl text-black-600 font-circular-bold">
          {t('withdraw.selectAmount.title')}
        </h2>

        <Center className="text-center" variant="horizontal">
          <h3 className="w-160 text-lg leading-6 text-black-400">
            <SkeletonBox className="h-12 w-full" loading={limitLoading}>
              {t(
                `withdraw.selectAmount.description.${
                  currency ?? Currency.USDC
                }`,
                {
                  duration,
                },
              )}
            </SkeletonBox>
          </h3>
        </Center>

        <Center className="mt-8" variant="horizontal">
          {isEntityWithdrawalLimits ? (
            <GraphQLErrorBoundary error={limitError} retry={limitRefetch}>
              <SelectAmountForm
                limitData={limitData?.entityLimits}
                limitLoading={limitLoading}
                onSubmit={handleAmountSubmit}
              />
            </GraphQLErrorBoundary>
          ) : (
            <SelectAmountFormLegacy onSubmit={handleAmountSubmit} />
          )}
        </Center>
      </Center>
    </FullScreen>
  )
}
